import { DateTime } from 'luxon';
import { FC, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import Button from '@/components/Button';
import {
  ApplyButton,
  Box,
  Break,
  Days,
  Header,
  MonthButton,
  MonthButtonHolder,
  WeekDay,
} from '@/components/Datepicker';
import FilterButton from '@/components/FilterDropdown/FilterButton';
import Text from '@/components/Text';
import useOnClickOutside from '@/hooks/useOnClickOutside';
import { buildCalendar } from '@/utils/buildCalendar';

const SelectButton = styled(Text)`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: 0.2s ease-out border-color;
  &:focus,
  &:hover {
    border-color: ${({ theme }) => theme.colors.gray};
  }
`;

export const StyledBox = styled(Box)`
  left: auto;
  right: 0;
  width: 320px;
  z-index: 10;
`;

export const BoxTop = styled.div`
  padding: 32px 30px 16px;
  width: 320px;
`;

export const BoxBottom = styled.div`
  padding: 8px 32px 16px;
  width: 320px;
`;

export const Relative = styled.div`
  position: relative;
`;

const ClearButton = styled(Button)`
  margin-top: 16px;
  width: 100%;
  &:disabled {
    border: 0;
    color: ${({ theme }) => theme.colors.gray};
  }
`;

export const SelectedDays = styled.div`
  width: 100%;
  padding: 11px;
  text-align: center;
  background-color: ${({ theme }) => theme.colors.grayscaleLight};
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  border-radius: 4px;
  grid-gap: 1px 0;
`;

interface DayProps {
  $isOtherMonthDay?: boolean;
  $isSelected?: boolean;
  $isDayinBetween?: boolean;
  $isRangeStart?: boolean;
  $isFutureDay?: boolean;
  $isRangeSelected?: boolean;
}

export const Day = styled(Text)<DayProps>`
  height: 32px;
  justify-content: center;
  display: flex;
  align-items: center;
  cursor: pointer;
  width: 100%;
  ${({ $isOtherMonthDay }) =>
    $isOtherMonthDay &&
    css`
      visibility: hidden;
    `}
  ${({ $isSelected, $isRangeStart, $isRangeSelected }) =>
    $isSelected &&
    css`
      position: relative;
      &:before {
        content: '';
        position: absolute;
        top: 0;
        left: ${$isRangeStart ? 'auto' : '0'};
        right: ${$isRangeStart ? '0' : 'auto'};
        width: ${$isRangeSelected ? '50%' : '0px'};
        height: 100%;
        background: ${({ theme }) => theme.colors.grayscaleLight};
        z-index: -1;
      }
      > span {
        opacity: 1;
        background: ${({ theme }) => theme.colors.error};
        color: ${({ theme }) => theme.colors.white};
        display: flex;
        width: 32px;
        height: 32px;
        justify-content: center;
        align-items: center;
        border-radius: 50%;
      }
    `};
  ${({ $isDayinBetween }) =>
    $isDayinBetween &&
    css`
      background: ${({ theme }) => theme.colors.grayscaleLight};
      border-radius: 0;
    `};
  ${({ $isFutureDay }) =>
    $isFutureDay &&
    css`
      opacity: 0.4;
      cursor: initial;
    `};
`;

interface Props {
  selectedDateRange: {
    rangeStart?: DateTime | undefined;
    rangeEnd?: DateTime | undefined;
  };
  setSelectedDateRange: React.Dispatch<
    React.SetStateAction<{
      rangeStart?: DateTime | undefined;
      rangeEnd?: DateTime | undefined;
    }>
  >;
  setActivePeriod?: React.Dispatch<React.SetStateAction<string>>;
  text?: string;
  allDaysSelectable?: boolean;
  className?: string;
  variant?: 'select';
  openDownwardOnly?: boolean;
}

const DateRangeFilter: FC<Props> = ({
  selectedDateRange,
  setSelectedDateRange,
  setActivePeriod,
  text = 'Date',
  allDaysSelectable = false,
  className,
  variant,
  openDownwardOnly,
}) => {
  const now = DateTime.now();

  const [localSelectedDateRange, setLocalSelectedDateRange] = useState<{
    rangeStart?: DateTime;
    rangeEnd?: DateTime;
  }>(selectedDateRange);

  useEffect(() => {
    setLocalSelectedDateRange(selectedDateRange);
  }, [selectedDateRange]);

  const [selectedDay, setSelectedDay] = useState<DateTime>(now);

  const [showDatePicker, setShowDatePicker] = useState(false);

  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setShowDatePicker(false));

  const calendar = buildCalendar(selectedDay);

  const getPreviousMonth = () => {
    return selectedDay.minus({ months: 1 });
  };

  const getNextMonth = () => {
    return selectedDay.plus({ months: 1 });
  };

  const getCurrentMonthName = () => {
    return selectedDay.toFormat('MMMM');
  };

  const getCurrentYear = () => {
    return selectedDay.toFormat('y');
  };

  const firstMothDay = selectedDay.startOf('month');

  const lastMonthDay = selectedDay.endOf('month');

  const [isFilterApplied, setIsFilterApplied] = useState(true);

  const changeRange = (day: DateTime) => {
    if (
      !!selectedDateRange.rangeStart &&
      !!selectedDateRange.rangeEnd &&
      isFilterApplied
    ) {
      if (day > now && !allDaysSelectable) {
        return;
      }
      return setLocalSelectedDateRange({
        rangeStart: day,
        rangeEnd: undefined,
      });
    }

    if (day > now && !allDaysSelectable) {
      return;
    }

    if (day.hasSame(now, 'day') && !localSelectedDateRange.rangeStart) {
      return setLocalSelectedDateRange({
        rangeStart: undefined,
        rangeEnd: day,
      });
    }

    if (!localSelectedDateRange.rangeStart) {
      setLocalSelectedDateRange((prev) => {
        return {
          rangeStart: day,
          rangeEnd: prev.rangeEnd,
        };
      });
    }

    if (
      day > localSelectedDateRange.rangeStart! &&
      localSelectedDateRange.rangeEnd
    ) {
      setLocalSelectedDateRange((prev) => {
        return { rangeStart: prev.rangeStart, rangeEnd: day };
      });
    }

    if (day > localSelectedDateRange.rangeStart!) {
      setLocalSelectedDateRange((prev) => {
        return { rangeStart: prev.rangeStart, rangeEnd: day };
      });
    }

    if (
      day < localSelectedDateRange.rangeStart! &&
      !localSelectedDateRange.rangeEnd
    ) {
      setLocalSelectedDateRange((prev) => {
        return { rangeStart: day, rangeEnd: prev.rangeStart };
      });
    }

    if (day < localSelectedDateRange.rangeStart!) {
      setLocalSelectedDateRange((prev) => {
        return { rangeStart: day, rangeEnd: prev.rangeEnd };
      });
    }
  };

  const selectButtonLabel = selectedDateRange.rangeStart
    ? `${selectedDateRange.rangeStart.toFormat('MMM d, y')}${selectedDateRange.rangeEnd ? ` – ${selectedDateRange.rangeEnd.toFormat('MMM d, y')}` : ''}`
    : 'Select';

  return (
    <>
      <Relative ref={ref}>
        {variant === 'select' ? (
          <SelectButton
            as="button"
            $type="smallBold"
            className={className}
            onClick={() => setShowDatePicker((prev) => !prev)}
          >
            {selectButtonLabel}
            <svg
              width="8"
              height="6"
              viewBox="0 0 8 6"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M4 5.5L8 0.5H0L4 5.5Z" fill="currentColor" />
            </svg>
          </SelectButton>
        ) : (
          <FilterButton
            className={className}
            onClick={() => setShowDatePicker((prev) => !prev)}
            isActive={showDatePicker}
            filterApplied={
              !!selectedDateRange.rangeStart || !!selectedDateRange.rangeEnd
            }
            text={text}
            type="date"
          />
        )}
        {showDatePicker && (
          <StyledBox
            style={
              variant === 'select' ? { right: 'auto', left: 0 } : undefined
            }
            $openUpward={
              !openDownwardOnly &&
              !!ref.current &&
              window.innerHeight - ref.current.getBoundingClientRect().bottom <
                400
            }
          >
            <BoxTop>
              <Header>
                <Text $type="caption">
                  {getCurrentMonthName()} {getCurrentYear()}
                </Text>
                <MonthButtonHolder>
                  <MonthButton
                    type="button"
                    onClick={() => setSelectedDay(getPreviousMonth())}
                  >
                    <svg
                      width="7"
                      height="12"
                      viewBox="0 0 7 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5.75729 1.75748L1.51465 6.00012L5.75729 10.2428"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </MonthButton>
                  <MonthButton
                    type="button"
                    onClick={() => setSelectedDay(getNextMonth())}
                  >
                    <svg
                      width="7"
                      height="12"
                      viewBox="0 0 7 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M1.75736 1.75748L6 6.00012L1.75736 10.2428"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </MonthButton>
                </MonthButtonHolder>
              </Header>
              <Days>
                <WeekDay $type="caption" $color="gray">
                  Mo
                </WeekDay>
                <WeekDay $type="caption" $color="gray">
                  Tu
                </WeekDay>
                <WeekDay $type="caption" $color="gray">
                  We
                </WeekDay>
                <WeekDay $type="caption" $color="gray">
                  Th
                </WeekDay>
                <WeekDay $type="caption" $color="gray">
                  Fr
                </WeekDay>
                <WeekDay $type="caption" $color="gray">
                  Sa
                </WeekDay>
                <WeekDay $type="caption" $color="gray">
                  Su
                </WeekDay>
              </Days>
              <Grid>
                {calendar.days.map((day: DateTime, index: number) => (
                  <Day
                    key={index}
                    $type="caption"
                    onClick={() => {
                      setIsFilterApplied(false);
                      changeRange(day);
                    }}
                    $isOtherMonthDay={day < firstMothDay || day > lastMonthDay}
                    $isRangeSelected={
                      !!localSelectedDateRange.rangeStart &&
                      !!localSelectedDateRange.rangeEnd
                    }
                    $isFutureDay={day > now && !allDaysSelectable}
                    $isDayinBetween={
                      day > localSelectedDateRange.rangeStart! &&
                      day < localSelectedDateRange.rangeEnd! &&
                      !localSelectedDateRange.rangeEnd?.hasSame(day, 'day')
                    }
                    $isRangeStart={localSelectedDateRange.rangeStart?.hasSame(
                      day,
                      'day',
                    )}
                    $isSelected={
                      localSelectedDateRange.rangeStart?.hasSame(day, 'day') ||
                      localSelectedDateRange.rangeEnd?.hasSame(day, 'day')
                    }
                  >
                    <span>{day.toFormat('d')}</span>
                  </Day>
                ))}
              </Grid>
            </BoxTop>
            <Break />
            {(localSelectedDateRange.rangeStart ||
              localSelectedDateRange.rangeEnd) && (
              <SelectedDays>
                <Text $type="caption">
                  {localSelectedDateRange.rangeStart?.toFormat('MMMM d, y')}{' '}
                  {localSelectedDateRange.rangeStart &&
                    localSelectedDateRange.rangeEnd &&
                    '- '}
                  {localSelectedDateRange.rangeEnd?.toFormat('MMMM d, y')}
                </Text>
              </SelectedDays>
            )}
            <BoxBottom>
              <ApplyButton
                onClick={() => {
                  setSelectedDateRange(localSelectedDateRange);
                  setShowDatePicker(false);
                  setIsFilterApplied(true);
                  !!setActivePeriod && setActivePeriod('');
                }}
              >
                Apply
              </ApplyButton>
              <ClearButton
                disabled={
                  !localSelectedDateRange.rangeStart &&
                  !localSelectedDateRange.rangeEnd
                }
                color="secondary"
                onClick={() => {
                  setLocalSelectedDateRange({
                    rangeStart: undefined,
                    rangeEnd: undefined,
                  });

                  setSelectedDateRange({
                    rangeEnd: undefined,
                    rangeStart: undefined,
                  });

                  !!setActivePeriod && setActivePeriod('All');
                }}
              >
                Clear
              </ClearButton>
            </BoxBottom>
          </StyledBox>
        )}
      </Relative>
    </>
  );
};

export default DateRangeFilter;
